import { JsonConverter, JsonDiscriminatorValue, JsonObject, JsonProperty, JsonType } from "ta-json";

import type { Nullable, WritablePart } from "../base-types";
import DataType, { PropertyDataType } from "../contracts/base/data-type";
import { JsonContentType } from "../contracts/base/json-content-type";
import { MemberDefinitionType } from "../contracts/base/member-definition-type";
import { StringContentType } from "../contracts/base/string-content-type";
import Link from "../link";
import {
    ConditionalValidationResource,
    ConditionalValidationResourceConverter,
} from "./conditional-validation-resource";
import { MemberDefinitionResource } from "./member-definition-resource";
@JsonObject()
export class PropertyDefinitionResource extends MemberDefinitionResource {
    @JsonProperty("is_indexed")
    indexed: boolean;

    @JsonProperty("is_mandatory")
    isMandatory: boolean;

    @JsonProperty("is_multilanguage")
    isMultiLanguage: boolean;

    @JsonProperty("is_multivalue")
    isMultiValue: boolean;

    @JsonProperty("is_unique")
    isUnique: boolean;

    @JsonProperty("boost")
    boost: boolean;

    @JsonProperty("included_in_content")
    includeInContent: boolean;

    @JsonProperty("included_in_completion")
    includeInCompletion: boolean;

    @JsonProperty("ignore_on_export")
    ignoreOnExport: boolean;

    @JsonProperty("stored_in_graph")
    storedInGraph: boolean;

    @JsonProperty("conditional_validation")
    @JsonConverter(ConditionalValidationResourceConverter)
    conditionalValidation?: ConditionalValidationResource;

    @JsonProperty("validation_expression")
    validationExpression: Nullable<string>;

    get definitionType(): MemberDefinitionType {
        return MemberDefinitionType.Property;
    }
}

@JsonObject()
@JsonDiscriminatorValue(DataType.Boolean)
export class BooleanPropertyDefinitionResource extends PropertyDefinitionResource {
    constructor(init?: Partial<WritablePart<BooleanPropertyDefinitionResource>>) {
        super(PropertyDataType.Boolean);
        Object.assign(this, init);
    }
}

@JsonObject()
@JsonDiscriminatorValue(DataType.DateTime)
export class DateTimePropertyDefinitionResource extends PropertyDefinitionResource {
    constructor(init?: Partial<WritablePart<DateTimePropertyDefinitionResource>>) {
        super(PropertyDataType.DateTime);
        Object.assign(this, init);
    }
}

@JsonObject()
@JsonDiscriminatorValue(DataType.DateTimeOffset)
export class DateTimeOffsetPropertyDefinitionResource extends PropertyDefinitionResource {
    constructor(init?: Partial<WritablePart<DateTimeOffsetPropertyDefinitionResource>>) {
        super(PropertyDataType.DateTimeOffset);
        Object.assign(this, init);
    }
}

@JsonObject()
@JsonDiscriminatorValue(DataType.Decimal)
export class DecimalPropertyDefinitionResource extends PropertyDefinitionResource {
    constructor(init?: Partial<WritablePart<DecimalPropertyDefinitionResource>>) {
        super(PropertyDataType.Decimal);
        Object.assign(this, init);
    }
}

@JsonObject()
@JsonDiscriminatorValue(DataType.Integer)
export class IntegerPropertyDefinitionResource extends PropertyDefinitionResource {
    constructor(init?: Partial<WritablePart<IntegerPropertyDefinitionResource>>) {
        super(PropertyDataType.Integer);
        Object.assign(this, init);
    }
}

@JsonObject()
@JsonDiscriminatorValue(DataType.Json)
export class JsonPropertyDefinitionResource extends PropertyDefinitionResource {
    @JsonProperty("content_type")
    contentType: Nullable<JsonContentType> = null;

    constructor(init?: Partial<WritablePart<JsonPropertyDefinitionResource>>) {
        super(PropertyDataType.Json);
        Object.assign(this, init);
    }
}

@JsonObject()
@JsonDiscriminatorValue(DataType.Long)
export class LongPropertyDefinitionResource extends PropertyDefinitionResource {
    constructor(init?: Partial<WritablePart<LongPropertyDefinitionResource>>) {
        super(PropertyDataType.Long);
        Object.assign(this, init);
    }
}

@JsonObject()
@JsonDiscriminatorValue(DataType.String)
export class StringPropertyDefinitionResource extends PropertyDefinitionResource {
    @JsonProperty("content_type")
    contentType: StringContentType = StringContentType.SingleLine;

    @JsonProperty("validation_expression")
    validationExpression: Nullable<string> = null;

    @JsonProperty("datasource")
    @JsonType(Link)
    dataSource: Nullable<Link> = null;

    constructor(init?: Partial<WritablePart<StringPropertyDefinitionResource>>) {
        super(PropertyDataType.String);
        Object.assign(this, init);
    }
}
